import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { Loader, useMantineTheme, Box } from '@mantine/core';

import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { TextInput } from '~/domains/design-system/TextInput';
import { Title } from '~/domains/design-system/Title';
import dt from '~/testing';

import { RecaptchaText } from '../RecaptchaText';

import useStyles from './PasswordActions.styles';

type MantinneFormFieldProps = {
  value: any;
  onChange: any;
  checked?: any;
  error?: any;
  onFocus?: any;
  onBlur?: any;
};
type SignInFormProps = {
  heading?: string;
  title: string;
  subtitle?: string;
  handleFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  emailFieldProps: MantinneFormFieldProps;
  handleSupportLinkClick: (unknown) => void;
  isFormLoading: boolean;
  isReset: boolean;
};

function ForgotPasswordForm({
  heading,
  title,
  subtitle,

  emailFieldProps,
  handleFormSubmit,
  handleSupportLinkClick,
  isFormLoading,
  isReset,
}: SignInFormProps) {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const { t } = useTranslation('auth');

  return (
    <div className={classes.container}>
      <div className={classes.forgotPasswordContainer}>
        <div className={classes.forgotPasswordHeader}>
          {heading && (
            <Title variant="subhead-small" c={theme.colors.blue[7]}>
              {heading}
            </Title>
          )}
          <Title variant="display-headline-expanded-xl" align="center" c={theme.colors.gray2[6]}>
            {title}
          </Title>
          {subtitle && (
            <Text variant="body-large" align="center" c={theme.colors.gray2[6]}>
              {subtitle}
            </Text>
          )}
        </div>
        <form className={classes.formContainer} onSubmit={handleFormSubmit}>
          <Box h={4} />
          <TextInput
            data-test-id={dt.auth.forgotPassword.emailInput}
            label={!isReset ? t('forgotPassword.fields.emailOrUsername') : ''}
            w="100%"
            className={classes.emailInput}
            hasBorder={false}
            disabled={isReset}
            {...emailFieldProps}
          />
          <Button
            data-test-id={dt.auth.forgotPassword.requestPasswordLinkButton}
            fullWidth
            variant="primary-fill"
            size="large"
            disabled={isFormLoading}
          >
            {isFormLoading ? (
              <Loader stroke={theme.white} size="sm" style={{ verticalAlign: 'bottom' }} />
            ) : (
              t('forgotPassword.buttonLabel')
            )}
          </Button>
        </form>
        {!isReset && (
          <Text variant="body-medium" ta="center" color={theme.colors.gray2[3]}>
            <Trans
              i18nKey="auth:forgotPassword.support"
              components={{
                link: (
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore Children are injected via <Trans> component
                  <Button
                    style={{
                      display: 'inline-block',
                      textDecoration: 'underline',
                      fontWeight: 400,
                    }}
                    mode="linkButton"
                    size="small"
                    onClick={handleSupportLinkClick}
                  />
                ),
              }}
            />
          </Text>
        )}
        <RecaptchaText />
      </div>
    </div>
  );
}

export default ForgotPasswordForm;
